import { FLOW_PATTERNS, archetypeForPatterns } from '@/guidance/flowPatterns';
import { categoryById, featureCatalog } from '@/guidance/capabilities';
import { vibeById } from '@/guidance/vibes';
import type { SelectedPlan } from '@/llm/planTypes';
import { patternDirectives } from '@/llm/styleDna';
export type { SelectedPlan };

export interface WizardInputs {
  name: string;
  description: string; // the goal
  flowPatterns: string[];
  flowNote: string;
  features: string[];
  vibe: string | null;
  /** A chosen plan-first blueprint + image-derived style; when present it drives the
   *  structure + look (and supersedes the preset-vibe wording). */
  plan?: SelectedPlan | null;
}

/** Seed directives derived from a chosen plan: the navigation/structure, the exact
 *  palette + mood from the vibe image, and any free-text tweak. */
function planDirectives({ option, styleBrief, tweak }: SelectedPlan): string[] {
  const navDesc = (option.nav === 'tabs' || option.nav === 'drawer') && option.tabs?.length
    ? `${option.nav} navigation (${option.tabs.join(', ')})`
    : `${option.nav} navigation`;
  const p = styleBrief.palette;
  const out = [
    `Structure: ${option.blueprint || option.title}. Use ${navDesc}; the main screen is a ${option.mainLayout} layout.`,
  ];
  if (option.screens.length) out.push(`Screens: ${option.screens.join(', ')}.`);
  out.push(
    `Visual style — use this exact palette: primary ${p.primary}${p.accent ? `, accent ${p.accent}` : ''}, ` +
    `background ${p.background}, text ${p.text}.` +
    (styleBrief.mood ? ` Mood: ${styleBrief.mood}.` : '') +
    (styleBrief.keywords.length ? ` Style: ${styleBrief.keywords.join(', ')}.` : ''),
  );
  const pat = patternDirectives(styleBrief.patterns);
  if (pat) out.push(pat);
  if (tweak && tweak.trim()) out.push(`Adjustment: ${tweak.trim()}.`);
  return out;
}

// Title-case the first few words of the goal as a default app name. Naive
// title-casing (lowercases after the first letter) is intentional for a suggestion.
export function suggestName(goal: string): string {
  const words = goal.trim().split(/\s+/).filter(Boolean).slice(0, 4);
  if (words.length === 0) return 'My App';
  return words.map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

export function buildScope(s: WizardInputs): { categoryLabel: string; featureLabels: string[] } {
  const archetype = archetypeForPatterns(s.flowPatterns);
  const featureLabels = featureCatalog.filter((f) => s.features.includes(f.id)).map((f) => f.label);
  return { categoryLabel: categoryById(archetype)?.label ?? 'Custom', featureLabels };
}

// The single seed message that primes discovery/generation. Used by both the
// wizard's direct build and the clarify chat's first message.
export function seedMessage(s: WizardInputs): string {
  const appName = s.name.trim() || suggestName(s.description);
  const patternLabels = FLOW_PATTERNS.filter((p) => s.flowPatterns.includes(p.id)).map((p) => p.label);
  const vibe = vibeById(s.vibe);
  const parts = [`I want to build an app called "${appName}". Goal: ${s.description}.`];
  if (patternLabels.length) parts.push(`Users will: ${patternLabels.join('; ')}.`);
  const hints = FLOW_PATTERNS.filter((p) => s.flowPatterns.includes(p.id) && p.seedHint).map((p) => p.seedHint!);
  if (hints.length) parts.push(...hints);
  if (s.flowNote.trim()) parts.push(s.flowNote.trim());
  // A chosen plan supersedes the preset-vibe wording (its style brief drives the look).
  if (vibe && !s.plan) parts.push(`Visual vibe: ${vibe.label} — ${vibe.description}.`);
  if (s.plan) parts.push(...planDirectives(s.plan));
  return parts.join(' ');
}
